// Ops · Catch-weight: estimate vs actual for every weighed line (docs/PHASE7-CONTRACT.md,
// contract.d.ts "Ops · Catch-weight"). Pure: node --test runs it.
import { peso } from '../../lib/format.js';

/** @typedef {import('@popcart/core/v1').OpsCatchWeight} OpsCatchWeight */
/** @typedef {import('@popcart/core/v1').CatchWeightLine} CatchWeightLine */

/**
 * @param {{ from: string; to: string }} period
 * @param {string | null} shopId
 */
export const catchWeightKey = (period, shopId) => ['ops', 'catch-weight', period.from, period.to, shopId ?? 'all'];

/** The query: the period, plus a shop when one is picked. @param {{ from: string; to: string }} period @param {string | null} shopId */
export function catchWeightParams(period, shopId) {
  return shopId ? { from: period.from, to: period.to, shopId } : { from: period.from, to: period.to };
}

/** "850 g", "1.25 kg". @param {number} g */
export function grams(g) {
  if (!Number.isFinite(g)) return '—';
  if (Math.abs(g) < 1000) return `${Math.round(g)} g`;
  return `${(Math.round(g / 10) / 100).toString()} kg`;
}

/** "+12.5%", "−4%", "0%" (one decimal at most). @param {number | null | undefined} pct */
export function signedPct(pct) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—';
  const r = Math.round(pct * 10) / 10;
  if (r === 0) return '0%';
  return `${r > 0 ? '+' : '−'}${Math.abs(r)}%`;
}

/** "+₱12", "−₱8.50", "₱0". @param {number} c */
export function signedPeso(c) {
  if (!Number.isFinite(c)) return '—';
  if (c === 0) return peso(0);
  return c > 0 ? `+${peso(c)}` : peso(c);
}

/**
 * One weighed line for the table: grams and money, the weight drift in percent and its tone.
 * Over the tolerance (capped) is a warning: the shop absorbed the rest.
 * @param {CatchWeightLine} l
 */
export function lineView(l) {
  const pct = l.estGrams > 0 ? ((l.actualGrams - l.estGrams) / l.estGrams) * 100 : null;
  return {
    est: grams(l.estGrams),
    actual: grams(l.actualGrams),
    variance: signedPct(pct),
    estPrice: peso(l.estLineCentavos),
    finalPrice: peso(l.finalLineCentavos),
    delta: signedPeso(l.deltaCentavos),
    tone: /** @type {'warning' | 'success' | 'info' | 'neutral'} */ (l.capped ? 'warning' : l.deltaCentavos < 0 ? 'success' : l.deltaCentavos > 0 ? 'info' : 'neutral'),
    capped: l.capped,
  };
}

/**
 * The shop filter's options: every shop that weighed in this period plus any shop from the list,
 * by name, de-duplicated.
 * @param {OpsCatchWeight | undefined} data
 * @param {{ id: string; name: string; lines?: string[] }[] | undefined} shops
 */
export function shopOptions(data, shops) {
  /** @type {Map<string, string>} */
  const m = new Map();
  for (const s of shops ?? []) if (!s.lines || s.lines.includes('fresh')) m.set(s.id, s.name);
  for (const s of data?.byShop ?? []) m.set(s.shopId, s.shopName);
  return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/** Most drift first. @param {OpsCatchWeight['byShop']} rows */
export function sortByShop(rows) {
  return [...rows].sort((a, b) => Math.abs(b.avgVariancePct) - Math.abs(a.avgVariancePct) || b.lines - a.lines || a.shopName.localeCompare(b.shopName));
}
